import QRCode from 'qrcode';

export type VerificationDocumentType =
  | 'medical_record'
  | 'assessment_result'
  | 'attendance_certificate'
  | 'medical_record_bundle';
export type VerificationStatus = 'valid' | 'invalidated';
export type VerificationScope = 'document' | 'file';
export type VerificationGenerationChannel = 'download' | 'print';

export interface SignedDocumentMetadata {
  verificationToken: string;
  documentHash: string;
  signedAt: string;
  signedByUserId: string;
  signedByName: string;
  signedByRole: string | null;
  signedByRegistration: string | null;
  templateVersion: number;
  method: 'metadata_qr_v1';
}

export interface PublicDocumentVerificationRecord {
  token: string;
  verificationScope?: VerificationScope;
  tenantSlug?: string | null;
  tenantName?: string | null;
  documentType: VerificationDocumentType;
  documentId?: string | null;
  documentTitle: string;
  patientId?: string | null;
  patientName?: string | null;
  patientMedicalRecordNumber?: string | null;
  professionalName?: string | null;
  professionalRole?: string | null;
  professionalRegistration?: string | null;
  signedAt: string;
  status: VerificationStatus;
  documentHash: string;
  fileHash?: string | null;
  sourceDocumentToken?: string | null;
  generatedAt?: string | null;
  generatedByUserId?: string | null;
  generatedByName?: string | null;
  generationChannel?: VerificationGenerationChannel | null;
  fileName?: string | null;
  fileSizeBytes?: number | null;
  templateVersion: number;
  snapshot?: Record<string, unknown>;
  updatedAt?: string | null;
}

export interface AttendanceCertificateVerificationInput {
  clientId: string;
  tenantSlug?: string | null;
  tenantName: string;
  documentTitle: string;
  patientId?: string | null;
  patientName: string;
  patientMedicalRecordNumber?: string | null;
  professionalName: string;
  professionalRole?: string | null;
  professionalRegistration?: string | null;
  signedAt: string;
  documentHash: string;
  templateVersion: number;
  snapshot?: Record<string, unknown>;
  token: string;
}

export interface GeneratedDocumentFileVerificationInput {
  clientId: string;
  tenantSlug?: string | null;
  tenantName?: string | null;
  documentType: VerificationDocumentType;
  documentId?: string | null;
  documentTitle: string;
  patientId?: string | null;
  patientName?: string | null;
  patientMedicalRecordNumber?: string | null;
  professionalName?: string | null;
  professionalRole?: string | null;
  professionalRegistration?: string | null;
  signedAt: string;
  status?: VerificationStatus;
  documentHash: string;
  templateVersion: number;
  snapshot?: Record<string, unknown>;
  fileToken: string;
  sourceDocumentToken?: string | null;
  fileHash: string;
  generatedAt: string;
  generatedByUserId?: string | null;
  generatedByName?: string | null;
  generationChannel: VerificationGenerationChannel;
  fileName?: string | null;
  fileSizeBytes?: number | null;
}

const asObject = (value: unknown): Record<string, any> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, any>;
};

const asOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

export const buildVerificationPath = (token: string, tenantSlug?: string | null): string => {
  const safeToken = String(token || '').trim();
  const safeSlug = String(tenantSlug || '').trim();

  if (!safeToken) {
    return '/verificar';
  }

  return safeSlug ? `/${safeSlug}/verificar/${safeToken}` : `/verificar/${safeToken}`;
};

export const buildVerificationUrl = (
  token: string,
  tenantSlug?: string | null,
  origin?: string | null,
): string => {
  const fallbackOrigin =
    origin ||
    (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '');

  return `${fallbackOrigin}${buildVerificationPath(token, tenantSlug)}`;
};

const sortSerializableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortSerializableValue);
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const source = value as Record<string, unknown>;
  return Object.keys(source)
    .sort((left, right) => left.localeCompare(right))
    .reduce<Record<string, unknown>>((accumulator, key) => {
      accumulator[key] = sortSerializableValue(source[key]);
      return accumulator;
    }, {});
};

const fallbackHash = (value: string): string => {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `fnv1a_${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

export const hashVerificationPayload = async (payload: unknown): Promise<string> => {
  const normalized = JSON.stringify(sortSerializableValue(payload));

  if (globalThis.crypto?.subtle) {
    const bytes = new TextEncoder().encode(normalized);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((item) => item.toString(16).padStart(2, '0'))
      .join('');
  }

  return fallbackHash(normalized);
};

const normalizeBinaryContent = async (
  value: ArrayBuffer | Uint8Array | Blob,
): Promise<Uint8Array> => {
  if (value instanceof Uint8Array) {
    return value;
  }

  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }

  return new Uint8Array(await value.arrayBuffer());
};

export const hashBinaryContent = async (
  value: ArrayBuffer | Uint8Array | Blob,
): Promise<string> => {
  const bytes = await normalizeBinaryContent(value);

  if (globalThis.crypto?.subtle) {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((item) => item.toString(16).padStart(2, '0'))
      .join('');
  }

  return fallbackHash(Array.from(bytes).join(','));
};

export const createVerificationQrDataUrl = async (url: string): Promise<string> =>
  QRCode.toDataURL(url, {
    margin: 0,
    width: 256,
    color: {
      dark: '#0f172a',
      light: '#ffffff',
    },
  });

export const generateVerificationToken = (prefix = 'rvh'): string => {
  if (globalThis.crypto?.randomUUID) {
    return `${prefix}_${globalThis.crypto.randomUUID().replace(/-/g, '')}`;
  }

  const randomChunk = Math.random().toString(36).slice(2);
  return `${prefix}_${Date.now().toString(36)}${randomChunk}`;
};

export const buildSignedDocumentMetadata = async (input: {
  hashPayload: unknown;
  signedByUserId: string;
  signedByName: string;
  signedByRole?: string | null;
  signedByRegistration?: string | null;
  templateVersion: number;
}): Promise<SignedDocumentMetadata> => ({
  verificationToken: generateVerificationToken(),
  documentHash: await hashVerificationPayload(input.hashPayload),
  signedAt: new Date().toISOString(),
  signedByUserId: input.signedByUserId,
  signedByName: input.signedByName,
  signedByRole: input.signedByRole ?? null,
  signedByRegistration: input.signedByRegistration ?? null,
  templateVersion: input.templateVersion,
  method: 'metadata_qr_v1',
});

export const getSignedDocumentMetadata = (value: unknown): SignedDocumentMetadata | null => {
  const source = asObject(value);
  if (!source) return null;

  const verificationToken = asOptionalString(source.verificationToken);
  const documentHash = asOptionalString(source.documentHash);
  const signedAt = asOptionalString(source.signedAt);
  const signedByUserId = asOptionalString(source.signedByUserId);
  const signedByName = asOptionalString(source.signedByName);

  if (!verificationToken || !documentHash || !signedAt || !signedByUserId || !signedByName) {
    return null;
  }

  return {
    verificationToken,
    documentHash,
    signedAt,
    signedByUserId,
    signedByName,
    signedByRole: asOptionalString(source.signedByRole),
    signedByRegistration: asOptionalString(source.signedByRegistration),
    templateVersion:
      typeof source.templateVersion === 'number'
        ? source.templateVersion
        : Number.parseInt(String(source.templateVersion || '1'), 10) || 1,
    method: 'metadata_qr_v1',
  };
};

export const getMedicalRecordSignatureMetadata = (
  recordAttachments: unknown,
): SignedDocumentMetadata | null =>
  getSignedDocumentMetadata(asObject(recordAttachments)?.signature);

export const getAssessmentSignatureMetadata = (
  assetsJson: unknown,
): SignedDocumentMetadata | null =>
  getSignedDocumentMetadata(asObject(assetsJson)?.signature);

export const isMedicalRecordSigned = (recordAttachments: unknown): boolean =>
  Boolean(getMedicalRecordSignatureMetadata(recordAttachments));

export const isAssessmentResultSigned = (assetsJson: unknown): boolean =>
  Boolean(getAssessmentSignatureMetadata(assetsJson));
